import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Box, TextField, Button, CircularProgress,
} from "@mui/material";
import { RuleFolderOutlined } from "@mui/icons-material";
import ApiCall from "../../utils/apicall";
import { showSuccessToast, showErrorToast } from "../../utils/Toast";
import { formatDuties } from "./dutiesFormat";

/**
 * DutiesAcknowledgment: blocking first-login gate. If the signed-in user has a
 * position whose Duties & Responsibilities they have not yet signed at the current
 * version, this presents the duties and requires a typed signature + date to accept.
 * Mounted once in DashboardLayout. Per AISC 207-25 §1.14 / ISO 9001:2015 §7.2.
 *
 * Display (2026-10-18): the stored text is shown as sections. The position title and
 * section headings are highlighted, "Reports to" is a labelled line, and each duty is its
 * own bulleted row with spacing. Presentation only: every stored line is shown once, in
 * order, word for word (dutiesFormat.js), so the signature still covers exactly the text
 * of the version sent back with it.
 */
const ORANGE = "#F88F1D";
const NAVY = "#1D2330";

export function DutiesText({ text }) {
  const blocks = useMemo(() => formatDuties(text), [text]);
  return (
    <Box component="article" aria-label="Duties and responsibilities">
      {blocks.map((b, i) => {
        if (b.kind === "title") {
          return (
            <Typography key={i} component="h3" sx={{ fontSize: "1.15rem", fontWeight: 800, color: NAVY, mb: 0.5 }}>
              {b.text}
            </Typography>
          );
        }
        if (b.kind === "meta") {
          return (
            <Typography key={i} sx={{ fontSize: "0.9rem", color: "text.secondary", fontWeight: 600, mb: 1.5 }}>
              {b.text}
            </Typography>
          );
        }
        if (b.kind === "note") {
          return (
            <Typography key={i} sx={{ fontSize: "0.88rem", fontStyle: "italic", color: "text.secondary",
              bgcolor: "#FFF7EC", borderLeft: `3px solid ${ORANGE}`, px: 1.5, py: 1, mb: 1.5, borderRadius: 1 }}>
              {b.text}
            </Typography>
          );
        }
        if (b.kind === "heading") {
          return (
            <Typography key={i} component="h4" sx={{
              fontSize: "0.82rem", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase",
              color: NAVY, bgcolor: "#FFF1DE", borderLeft: `4px solid ${ORANGE}`,
              px: 1.25, py: 0.75, mt: i === 0 ? 0 : 2.5, mb: 1, borderRadius: 1,
            }}>
              {b.text}
            </Typography>
          );
        }
        return (
          <Box key={i} sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", py: 0.6, pl: 0.5,
            borderBottom: "1px solid #EEF0F3" }}>
            {/^[-•*]\s/.test(b.text) ? null : (
              <Box aria-hidden sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: ORANGE, mt: "0.55em", flex: "0 0 6px" }} />
            )}
            <Typography sx={{ fontSize: "0.92rem", lineHeight: 1.55, color: "#1F2937" }}>{b.text}</Typography>
          </Box>
        );
      })}
    </Box>
  );
}

export default function DutiesAcknowledgment() {
  const [pending, setPending] = useState(null);
  const [signed, setSigned] = useState("");
  const [saving, setSaving] = useState(false);
  const today = new Date().toLocaleDateString();

  const loadPending = async () => {
    const res = await ApiCall.get("/auth/duties-acknowledgment/pending");
    const d = res?.data?.data;
    return d?.needed ? d : null;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await loadPending();
        if (!cancelled && d) setPending(d);
      } catch { /* no gate on error */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!pending) return null;

  const submit = async () => {
    if (!signed.trim()) return;
    setSaving(true);
    try {
      // S-58: send the version shown, so the record holds exactly what was accepted.
      await ApiCall.post("/auth/duties-acknowledgment", {
        role_id: pending.role_id, signed_name: signed.trim(), duties_version: pending.duties_version,
      });
      showSuccessToast("Position accepted. Welcome aboard.");
      setPending(null);
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Could not record your acknowledgment.");
      if (err?.response?.status === 409 || err?.response?.status === 403) {
        // 409: the duties changed while this was open, so show the new version and ask for a new signature.
        // 403: the position is no longer assigned, so move to the next one (or close) instead of a dead end.
        try { const d = await loadPending(); setPending(d); setSigned(""); } catch { /* keep the gate */ }
      }
      setSaving(false);
    }
  };

  return (
    <Dialog open fullWidth maxWidth="md" disableEscapeKeyDown onClose={() => {}}>
      <DialogTitle sx={{ bgcolor: NAVY, color: "#fff", display: "flex", alignItems: "center", gap: 1 }}>
        <RuleFolderOutlined sx={{ color: ORANGE }} />
        <span>Accept your position: <Box component="span" sx={{ color: ORANGE, fontWeight: 800 }}>{pending.role_name}</Box></span>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          Please review the duties &amp; responsibilities for your position below. Sign and date to accept the
          position. You must accept before continuing.
        </Typography>
        <Box sx={{
          border: "1px solid #E3E6EA", borderRadius: 1.5, px: 2.5, py: 2,
          bgcolor: "#FBFCFD", maxHeight: "min(52vh, 520px)", overflow: "auto", mb: 2,
        }}>
          <DutiesText text={pending.duties} />
        </Box>
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end" }}>
          <TextField label="Signature (type your full name)" value={signed} autoFocus fullWidth size="small"
            onChange={(e) => setSigned(e.target.value)} />
          <TextField label="Date" value={today} size="small" InputProps={{ readOnly: true }} sx={{ width: 160 }} />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button variant="contained" disabled={!signed.trim() || saving} onClick={submit}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ bgcolor: ORANGE, "&:hover": { bgcolor: "#d97a10" }, fontWeight: 700, textTransform: "none" }}>
          {saving ? "Recording..." : "I accept and sign"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
